import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Pagination,
  Tooltip,
  Snackbar,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import GenericService from "../../services/GenericService";
import { isAdmin } from "../../services/AuthService";

interface Email {
  id: string;
  email: string;
  created_at: string;
  updated_at: string;
}

const EmailManagement: React.FC = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalEmails, setTotalEmails] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const [formData, setFormData] = useState({
    email: "",
  });
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await GenericService.getEmails({
        page: currentPage,
        per_page: 10,
        search: searchTerm,
      });
      setEmails(response.data || []);
      setTotalEmails(response.total || 0);
    } catch (error) {
      console.error("Error fetching emails:", error);
      showSnackbar("Failed to fetch emails", "error");
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    const checkAdminAccess = () => {
      try {
        const adminAccess = isAdmin();
        setHasAdminAccess(adminAccess);
        if (!adminAccess) {
          navigate("/");
        }
      } catch (err) {
        setHasAdminAccess(false);
      }
    };

    checkAdminAccess();
    fetchEmails();
  }, [navigate, currentPage, searchTerm, fetchEmails]);

  const handleCreateEmail = async () => {
    try {
      await GenericService.createEmail(formData);
      showSnackbar("Email created successfully", "success");
      setCreateDialogOpen(false);
      resetForm();
      fetchEmails();
    } catch (error: any) {
      console.error("Error creating email:", error);
      showSnackbar(error.message || "Failed to create email", "error");
    }
  };

  const handleEditEmail = async () => {
    if (!selectedEmail) return;
    try {
      await GenericService.updateEmail(selectedEmail.id, formData);
      showSnackbar("Email updated successfully", "success");
      setEditDialogOpen(false);
      resetForm();
      fetchEmails();
    } catch (error: any) {
      console.error("Error updating email:", error);
      showSnackbar(error.message || "Failed to update email", "error");
    }
  };

  const handleDeleteEmail = async () => {
    if (!selectedEmail) return;
    try {
      await GenericService.deleteEmail(selectedEmail.id);
      showSnackbar("Email deleted successfully", "success");
      setDeleteDialogOpen(false);
      fetchEmails();
    } catch (error: any) {
      console.error("Error deleting email:", error);
      showSnackbar(error.message || "Failed to delete email", "error");
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
    });
    setSelectedEmail(null);
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (email: Email) => {
    setSelectedEmail(email);
    setFormData({
      email: email.email,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (email: Email) => {
    setSelectedEmail(email);
    setDeleteDialogOpen(true);
  };

  const openViewDialog = (email: Email) => {
    setSelectedEmail(email);
    setViewDialogOpen(true);
  };

  if (!hasAdminAccess) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access Denied: You don't have administrator privileges.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: 4, mb: 4 }}
      data-testid="email-management"
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin?tab=0")}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Email Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Create Email
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Search and Filters */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Emails"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by email address..."
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchEmails}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Emails Table */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email Address</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emails.map((email) => (
                    <TableRow key={email.id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                          {email.email}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(email.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(email.updated_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => openViewDialog(email)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Email">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(email)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Email">
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(email)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <Pagination
                count={Math.ceil(totalEmails / 10)}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Create Email Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateEmail} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditEmail} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Email Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Email</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete the email:{" "}
            <strong>{selectedEmail?.email}</strong>? This action cannot be
            undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEmail} variant="outlined">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Email Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Email Details</DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {selectedEmail.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Created:</strong>{" "}
                {new Date(selectedEmail.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Updated:</strong>{" "}
                {new Date(selectedEmail.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmailManagement;
